import { LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { analyticsViewStyles } from './analytics-view.css';
import { renderAnalyticsViewPresentation } from './analytics-view-presentation';
import {
  AnalyticsDashboardData,
  PostAnalytics,
  GeographicEntry,
  ReferrerEntry,
  RecentPageView,
} from '../../types/analytics';

const TIMEZONE_REGION_MAP: Record<string, string> = {
  'America/New_York': 'New York, US',
  'America/Chicago': 'Chicago, US',
  'America/Denver': 'Denver, US',
  'America/Los_Angeles': 'Los Angeles, US',
  'America/Phoenix': 'Phoenix, US',
  'America/Toronto': 'Toronto, Canada',
  'America/Vancouver': 'Vancouver, Canada',
  'Europe/London': 'London, UK',
  'Europe/Paris': 'Paris, France',
  'Europe/Berlin': 'Berlin, Germany',
  'Europe/Amsterdam': 'Amsterdam, Netherlands',
  'Asia/Tokyo': 'Tokyo, Japan',
  'Asia/Singapore': 'Singapore',
  'Asia/Hong_Kong': 'Hong Kong',
  'Australia/Sydney': 'Sydney, Australia',
};

function formatTimezoneRegion(tz: string): string {
  if (TIMEZONE_REGION_MAP[tz]) return TIMEZONE_REGION_MAP[tz];
  return tz.replace('_', ' ');
}

@customElement('analytics-view')
export class AnalyticsView extends LitElement {
  static styles = analyticsViewStyles;

  @state()
  private isUnlocked = false;

  @state()
  private passwordInput = '';

  @state()
  private showPassword = false;

  @state()
  private hasError = false;

  @state()
  private errorMessage = '';

  @state()
  private isLoading = false;

  @state()
  private analyticsData: AnalyticsDashboardData | null = null;

  private handlePasswordInput(value: string) {
    this.passwordInput = value;
    this.hasError = false;
    this.errorMessage = '';
  }

  private handleToggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  private async handleUnlockSubmit() {
    if (!this.passwordInput.trim()) {
      this.hasError = true;
      this.errorMessage = 'Please enter a password.';
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const apiUrl = isDev
      ? 'http://127.0.0.1:5001/geireann/us-central1/getAnalytics'
      : 'https://us-central1-geireann.cloudfunctions.net/getAnalytics';

    try {
      // 1. Try Cloud Function proxy
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: this.passwordInput }),
      });

      if (response.ok) {
        const data = await response.json();
        this.analyticsData = data;
        this.isUnlocked = true;
        return;
      }

      if (response.status === 401) {
        this.hasError = true;
        this.errorMessage = 'Incorrect password. Please try again.';
        return;
      }
    } catch (err) {
      // Cloud function not reachable (e.g. not deployed on free Spark plan) — fallback to Firestore REST
    }

    // 2. Fallback: Direct Firestore REST API fetch & client-side aggregation
    try {
      const data = await this.fetchDirectFromFirestore();
      this.analyticsData = data;
      this.isUnlocked = true;
    } catch (err) {
      console.error('Failed to load analytics:', err);
      this.hasError = true;
      this.errorMessage = 'Unable to fetch analytics telemetry.';
    } finally {
      this.isLoading = false;
    }
  }

  private async fetchDirectFromFirestore(): Promise<AnalyticsDashboardData> {
    const firestoreUrl =
      'https://firestore.googleapis.com/v1/projects/geireann/databases/(default)/documents/analytics_pageviews?pageSize=300';
    const res = await fetch(firestoreUrl);
    if (!res.ok) throw new Error('Firestore REST read failed');

    const json = await res.json();
    const rawDocs = json.documents || [];

    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const postViews: Record<string, { title: string; count: number }> = {};
    const deviceBreakdown: Record<string, number> = {};
    const geoBreakdown: Record<string, number> = {};
    const referrerBreakdown: Record<string, number> = {};
    const recentActivity: RecentPageView[] = [];

    let totalViews = 0;
    let last7DaysViews = 0;
    let last30DaysViews = 0;
    const timezonesSet = new Set<string>();

    for (const doc of rawDocs) {
      const f = doc.fields || {};
      const path = f.path?.stringValue || '/';
      const title = f.title?.stringValue || path;
      const deviceType = f.deviceType?.stringValue || 'unknown';
      const timeZone = f.timeZone?.stringValue || 'unknown';
      const referrer = f.referrer?.stringValue || 'direct';
      const timestamp = f.timestamp?.stringValue || new Date().toISOString();

      totalViews++;
      const docDate = new Date(timestamp);
      if (docDate >= sevenDaysAgo) last7DaysViews++;
      if (docDate >= thirtyDaysAgo) last30DaysViews++;

      if (timeZone !== 'unknown') timezonesSet.add(timeZone);

      if (!postViews[path]) {
        postViews[path] = { title, count: 0 };
      }
      postViews[path].count++;

      deviceBreakdown[deviceType] = (deviceBreakdown[deviceType] || 0) + 1;
      geoBreakdown[timeZone] = (geoBreakdown[timeZone] || 0) + 1;

      if (referrer && referrer !== 'direct') {
        referrerBreakdown[referrer] = (referrerBreakdown[referrer] || 0) + 1;
      }

      recentActivity.push({
        path,
        title,
        timestamp,
        deviceType,
        timeZone,
      });
    }

    recentActivity.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    const perPost: PostAnalytics[] = Object.entries(postViews)
      .map(([p, item]) => ({
        path: p,
        title: item.title,
        views: item.count,
        percentage: totalViews > 0 ? (item.count / totalViews) * 100 : 0,
      }))
      .sort((a, b) => b.views - a.views);

    const geographicBreakdown: GeographicEntry[] = Object.entries(geoBreakdown)
      .map(([tz, count]) => ({
        timezone: tz,
        region: formatTimezoneRegion(tz),
        count,
      }))
      .sort((a, b) => b.count - a.count);

    const topReferrers: ReferrerEntry[] = Object.entries(referrerBreakdown)
      .map(([domain, count]) => ({ domain, count }))
      .sort((a, b) => b.count - a.count);

    return {
      totalViews,
      last7DaysViews,
      last30DaysViews,
      uniqueTimezones: timezonesSet.size,
      perPost,
      deviceBreakdown,
      geographicBreakdown,
      topReferrers,
      recentActivity: recentActivity.slice(0, 50),
    };
  }

  private handleBackToHome() {
    window.history.pushState(null, '', '/about');
    window.dispatchEvent(new Event('popstate'));
  }

  render() {
    return renderAnalyticsViewPresentation({
      isUnlocked: this.isUnlocked,
      isLoading: this.isLoading,
      passwordInput: this.passwordInput,
      showPassword: this.showPassword,
      hasError: this.hasError,
      errorMessage: this.errorMessage,
      data: this.analyticsData,
      onPasswordInput: this.handlePasswordInput.bind(this),
      onToggleShowPassword: this.handleToggleShowPassword.bind(this),
      onUnlockSubmit: this.handleUnlockSubmit.bind(this),
      onBackToHome: this.handleBackToHome.bind(this),
    });
  }
}
